import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import logo from '../../logo/lo.png';
import { crm } from './crmApi';
import DayStart from './DayStart';
import LeadDesk from './LeadDesk';
import SalesOverview from './SalesOverview';
import { CrmFollowUps, CrmMyLeads, CrmToday } from './SalesCrm';
import { CrmAddLeads, CrmRulesPage } from './ManagerCrm';
import { ManagerLeads, ManagerMonitor, ManagerRequests, ManagerTeam, MeetingsHub, MyCalls, SalesProgress, SalesRequests } from './Hubs';
import P2PBoard from './P2P';
import { authOf, bottomCount, clearCrmSession, homePage, loadCrmSession, pagesFor, resolvePage, saveCrmSession, takesCalls } from './session';
import type { CrmPage } from './session';
import { forgetCrmMe } from './ui';

// ── The Sales CRM site (/crm/<page>?tab=) ────────────────────
// Its own sign-in and a short menu per role (session.ts pagesFor); pages that
// belong together are tabs inside one item (Hubs.tsx). On a computer the menu
// is on the left; on a phone it is a bar at the bottom, the rest under More.
// The counts on the menu (crmBadges) say where something needs doing.

type Badges = { today: number; requests: number; waiting: number; meetings?: number; p2p?: number };

const CrmApp: React.FC = () => {
  const { page } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState(loadCrmSession);
  const [moreOpen, setMoreOpen] = useState(false);
  const [badges, setBadges] = useState<Badges | null>(null);
  const auth = useMemo(() => (session ? authOf(session) : null), [session]);
  const params = new URLSearchParams(search);
  const tab = params.get('tab') || undefined;
  const stageParam = params.get('stage') || undefined;

  const signOut = (why?: string) => {
    clearCrmSession();
    forgetCrmMe();
    setSession(null);
    if (why) toast.info(why);
    navigate('/crm/login', { replace: true });
  };

  // An expired sign-in (passcode changed, account made inactive) anywhere on the site
  useEffect(() => {
    const onExpired = () => signOut('Your sign-in has expired. Sign in again.');
    window.addEventListener('crm-auth-expired', onExpired);
    return () => window.removeEventListener('crm-auth-expired', onExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { setMoreOpen(false); window.scrollTo(0, 0); }, [page]);

  // The menu's counts, every minute while the page is open
  const loadBadges = useCallback(async () => {
    if (!auth) return;
    try { setBadges(await crm.badges(auth)); } catch { /* the pages show their own errors */ }
  }, [auth]);
  useEffect(() => {
    loadBadges();
    const t = setInterval(() => { if (document.visibilityState === 'visible') loadBadges(); }, 60000);
    return () => clearInterval(t);
  }, [loadBadges, page]);

  if (!session || !auth) return <Navigate to="/crm/login" replace />;
  const pages = pagesFor(session);
  const current = pages.find(p => p.id === page);
  if (!current) {
    // An old page name (a bookmark, a notification): where it lives now
    const moved = page ? resolvePage(session, page) : null;
    if (moved) {
      const q = new URLSearchParams();
      if (moved.tab || tab) q.set('tab', (moved.tab || tab)!);
      if (moved.stage || stageParam) q.set('stage', moved.stage || stageParam || '');
      return <Navigate to={`/crm/${moved.page}${q.toString() ? `?${q}` : ''}`} replace />;
    }
    return <Navigate to={`/crm/${homePage(session)}`} replace />;
  }

  // Open a page: its id, an old page name, or 'page:tab'; stage opens My Leads on one group
  const go = (target: string, stage?: string) => {
    const [id, t] = target.split(':');
    const own = pages.find(p => p.id === id);
    const where = own ? { page: id, tab: t } : resolvePage(session, id) || { page: homePage(session) };
    const q = new URLSearchParams();
    if (where.tab || t) q.set('tab', (where.tab || t)!);
    if (stage || ('stage' in where && where.stage)) q.set('stage', stage || (where as { stage?: string }).stage || '');
    navigate(`/crm/${where.page}${q.toString() ? `?${q}` : ''}`);
  };
  const onTab = (t: string) => navigate(`/crm/${current.id}?tab=${encodeURIComponent(t)}`, { replace: true });
  const hub = { auth, tab, onTab, onNavigate: go };
  const leadManager = session.role === 'Lead Manager';
  // A Lead Manager / Sales Admin decides whether they take new leads too (the Lead Desk's switch)
  const self = session.kind === 'sales' ? {
    takesLeads: session.takesLeads,
    onChange: (v: boolean) => { const next = { ...session, takesLeads: v }; saveCrmSession(next); forgetCrmMe(); setSession(next); },
  } : undefined;

  const body = (() => {
    switch (current.id) {
      // Salespeople
      case 'today':     return <CrmToday auth={auth} name={session.name} onNavigate={go} />;
      case 'leads':     return session.isManager ? <ManagerLeads {...hub} /> : <CrmMyLeads key={stageParam || ''} auth={auth} initialStage={stageParam} />;
      case 'followups': return <CrmFollowUps auth={auth} />;
      case 'progress':  return <SalesProgress {...hub} />;
      case 'requests':  return session.isManager ? <ManagerRequests {...hub} onChanged={loadBadges} /> : <SalesRequests auth={auth} />;
      case 'meetings':  return <MeetingsHub {...hub} isManager={session.isManager} />;
      case 'p2p':       return <P2PBoard auth={auth} isManager={session.isManager} isAdmin={session.kind === 'admin'} tab={tab} onTab={onTab} />;
      // Lead Managers, Sales Admins, the admin
      case 'home':      return leadManager ? <LeadDesk auth={auth} name={session.name} onNavigate={go} self={self} /> : <SalesOverview auth={auth} name={session.name} onNavigate={go} />;
      case 'add':       return <CrmAddLeads auth={auth} />;
      case 'team':      return <ManagerTeam {...hub} isAdmin={session.kind === 'admin'} />;
      case 'calls':     return <MyCalls {...hub} name={session.name} />;
      case 'monitor':   return <ManagerMonitor {...hub} name={session.name} leadManager={leadManager} self={self} />;
      case 'rules':     return <CrmRulesPage auth={auth} onNavigate={go} />;
      default:          return null;
    }
  })();

  const count = (p: CrmPage) => (p.badge && badges ? badges[p.badge] || 0 : 0);
  const badge = (n: number) => (n > 0 ? (
    <span className="min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold leading-5 text-center bg-red-500 text-white" aria-label={`${n} to do`}>{n > 99 ? '99+' : n}</span>
  ) : null);

  const sidebar = (
    <nav className="flex flex-col h-full" aria-label="Sales CRM">
      <div className="px-5 pt-5 pb-4 flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
          <img src={logo} alt="" className="w-[170%] max-w-none h-auto" />
        </span>
        <div>
          <p className="text-white font-bold leading-tight">Sales CRM</p>
          <p className="text-white/50 text-[11px]">VistaTech Solutions</p>
        </div>
      </div>
      <div className="mx-4 mb-4 rounded-xl bg-white/5 px-3 py-2.5">
        <p className="text-white text-sm font-semibold truncate">{session.name}</p>
        <p className="text-[11px] font-semibold" style={{ color: '#F0B429' }}>{session.role}{session.kind === 'sales' && session.isManager && session.takesLeads ? ' · takes new leads' : ''}</p>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
        {pages.map(p => {
          const on = p.id === current.id;
          return (
            <button key={p.id} onClick={() => go(p.id)} aria-current={on ? 'page' : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-left transition-colors ${on ? 'bg-white text-gray-900 font-bold' : 'text-white/80 hover:bg-white/10'}`}>
              <span className="text-base" aria-hidden>{p.emoji}</span>
              <span className="flex-1">{p.label}</span>
              {badge(count(p))}
            </button>
          );
        })}
      </div>
      <div className="p-3 border-t border-white/10">
        <button onClick={() => signOut()} className="w-full px-3 py-2 rounded-xl text-sm font-semibold text-white/70 hover:bg-white/10 text-left">⎋ Sign out</button>
      </div>
    </nav>
  );

  // The phone's bottom bar: the first few pages, then More for the rest
  const shown = pages.length > bottomCount(session) + 1 ? pages.slice(0, bottomCount(session)) : pages;
  const rest = pages.filter(p => !shown.includes(p));
  const restCount = rest.reduce((n, p) => n + count(p), 0);
  const bottomBar = (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)]" aria-label="Sales CRM">
      <div className="flex">
        {[...shown, ...(rest.length ? [{ id: '__more', label: 'More', short: 'More', emoji: '☰' } as CrmPage] : [])].map(p => {
          const isMore = p.id === '__more';
          const on = isMore ? rest.some(r => r.id === current.id) || moreOpen : p.id === current.id;
          const n = isMore ? restCount : count(p);
          return (
            <button key={p.id} onClick={() => (isMore ? setMoreOpen(o => !o) : go(p.id))} aria-current={!isMore && on ? 'page' : undefined}
              className={`flex-1 min-w-0 flex flex-col items-center gap-0.5 pt-2 pb-1.5 text-[11px] font-semibold relative ${on ? 'text-purple-800' : 'text-gray-500'}`}>
              <span className={`text-lg leading-none ${on ? '' : 'opacity-70'}`} aria-hidden>{p.emoji}</span>
              <span className="truncate max-w-full px-1">{p.short}</span>
              {on && <span className="absolute top-0 left-1/4 right-1/4 h-0.5 rounded-full bg-purple-700" />}
              {n > 0 && <span className="absolute top-1 right-[calc(50%-22px)] min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-4">{n > 99 ? '99+' : n}</span>}
            </button>
          );
        })}
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen" style={{ background: '#f5f4f9' }}>
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-60 z-30" style={{ background: 'linear-gradient(180deg,#1a1428,#2b2240)' }}>{sidebar}</aside>
      <div className="lg:pl-60">
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-100">
          <span className="w-8 h-8 rounded-lg bg-white border border-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
            <img src={logo} alt="" className="w-[170%] max-w-none h-auto" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-800 truncate leading-tight">{current.label}</p>
            <p className="text-[11px] text-gray-400 truncate">{session.name} · {session.role}</p>
          </div>
          <button onClick={() => signOut()} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600">Sign out</button>
        </header>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 pb-28 lg:pb-16">{body}</main>
        {/* Once a day: polls to update and the day's follow-ups */}
        {takesCalls(session) && <DayStart auth={auth} who={session.kind === 'sales' ? session.id : ''} onNavigate={go} />}
      </div>
      {bottomBar}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-20" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-x-0 bottom-[60px] mx-3 mb-2 rounded-2xl bg-white shadow-2xl p-2" onClick={e => e.stopPropagation()} role="menu" aria-label="More pages">
            {rest.map(p => (
              <button key={p.id} role="menuitem" onClick={() => { setMoreOpen(false); go(p.id); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-left ${p.id === current.id ? 'bg-purple-50 text-purple-900 font-bold' : 'text-gray-700 hover:bg-gray-50'}`}>
                <span className="text-lg" aria-hidden>{p.emoji}</span>
                <span className="flex-1">{p.label}</span>
                {badge(count(p))}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmApp;
